import type { ReactNode } from 'react';
import { CheckmarkFilled, CloseFilled } from '@carbon/react/icons';
import {
  FloatingTooltip,
  type FloatingTooltipProps,
} from 'components/floating-tooltip/floating-tooltip';
import { Date } from 'components/date';

export type TypeCurveVerificationStatus = string | null | undefined;

// The format every other timestamp on the Type Curve details page uses, so the tooltip and the
// "Last Verification Date" field read the same.
const VERDICT_DATE_FORMAT = 'M/DD/YYYY h:mm A';

interface TypeCurveVerificationIconClasses {
  correct?: string;
  incorrect?: string;
  idle?: string;
}

interface TypeCurveVerificationStatusIndicatorProps {
  status: TypeCurveVerificationStatus;
  showLabel?: boolean;
  hoverVerifiedBy?: boolean;
  lastVerifiedBy?: string | null;
  // Who gave this verdict and when. Distinct from `lastVerifiedBy`, which names whoever promoted
  // the workspace. When present the tooltip reads "Marked Correct by <name>" over the date.
  markedBy?: string | null;
  markedAt?: string | null;
  // Which side the tooltip opens on. A badge sitting near the top of the page wants 'bottom', so it
  // opens away from the nav bar rather than under it.
  tooltipPlacement?: FloatingTooltipProps['placement'];
  className?: string;
  labelClassName?: string;
  iconClasses?: TypeCurveVerificationIconClasses;
}

const normalizeVerificationStatus = (
  status: TypeCurveVerificationStatus
): string => String(status ?? '').toLowerCase();

export const typeCurveVerificationIsDecided = (
  status: TypeCurveVerificationStatus
): boolean => {
  const n = normalizeVerificationStatus(status);
  return n === 'correct' || n === 'incorrect';
};

export const typeCurveVerificationLabel = (
  status: TypeCurveVerificationStatus
): string => {
  const normalized = normalizeVerificationStatus(status);
  if (normalized === 'correct') return 'Correct';
  if (normalized === 'incorrect') return 'Incorrect';
  return '--';
};

// "Marked Correct by Jane Doe" over the moment the verdict was given, so a reader knows who stands
// behind the badge without opening the workspace.
const verdictTooltipContent = (
  status: TypeCurveVerificationStatus,
  actorName: string,
  markedAt?: string | null
): ReactNode => (
  <>
    {`Marked ${typeCurveVerificationLabel(status)} by ${actorName}`}
    {markedAt ? (
      <>
        <br />
        <Date date={markedAt} format={VERDICT_DATE_FORMAT} showTime />
      </>
    ) : null}
  </>
);

export const TypeCurveVerificationStatusIndicator = ({
  status,
  showLabel = false,
  hoverVerifiedBy = false,
  lastVerifiedBy,
  markedBy,
  markedAt,
  tooltipPlacement = 'top',
  className,
  labelClassName,
  iconClasses,
}: TypeCurveVerificationStatusIndicatorProps) => {
  const normalized = normalizeVerificationStatus(status);

  if (normalized !== 'correct' && normalized !== 'incorrect') {
    return null;
  }

  const icon =
    normalized === 'correct' ? (
      <CheckmarkFilled className={iconClasses?.correct} />
    ) : (
      <CloseFilled className={iconClasses?.incorrect} />
    );

  const verifierName =
    typeof lastVerifiedBy === 'string' ? lastVerifiedBy.trim() : '';
  const verdictActorName = typeof markedBy === 'string' ? markedBy.trim() : '';

  // The verdict actor tells the fuller story, so it wins when the caller carries it. Callers that
  // only have the promoter's name keep the shorter "Verified by" wording.
  const tooltipContent: ReactNode = verdictActorName
    ? verdictTooltipContent(status, verdictActorName, markedAt)
    : verifierName.length > 0
      ? `Verified by ${verifierName}`
      : null;

  const showVerifiedByTooltip = hoverVerifiedBy && tooltipContent !== null;

  const withTooltip = (trigger: ReactNode) => (
    <FloatingTooltip content={tooltipContent} placement={tooltipPlacement}>
      {trigger}
    </FloatingTooltip>
  );

  // With the label rendered the whole badge is the hover target, since aiming at a 16px icon beside
  // its own label reads as broken. Icon-only callers keep the icon as the trigger.
  const wrappedIcon =
    showVerifiedByTooltip && !showLabel
      ? withTooltip(<span>{icon}</span>)
      : icon;

  const indicator = (
    <span className={className}>
      {wrappedIcon}
      {showLabel ? (
        <span className={labelClassName}>
          {typeCurveVerificationLabel(status)}
        </span>
      ) : null}
    </span>
  );

  return showVerifiedByTooltip && showLabel
    ? withTooltip(indicator)
    : indicator;
};
